import { createContext, useContext } from 'react'
import type { Schema } from '@/api/client'

/**
 * Whose page this is.
 *
 * The app runs on two kinds of host (spec: *Hosts*). On the apex —
 * `augrank.com` — it is AugRank's own: the lockup, the six bullets on the login
 * panel, *Start a free trial*. On a tenant host — `<slug>.augrank.com` or a
 * verified custom domain — it is white-labelled: the organisation's logo or
 * name where the lockup was, its name in the tab title and on the favicon, and
 * nothing that invites a stranger to sign up.
 *
 * The API decides which, from the host the request arrived on
 * (`GET /api/v1/branding`), and this is that answer in the SPA's own casing.
 * `unknown` is a tenant-shaped host that is nobody's — the retired `app.`
 * alias, a typo — and `AppRoutes` renders `NoWorkspace` for it rather than
 * any of the five bands, regardless of path.
 */
export interface Brand {
  host: 'apex' | 'tenant' | 'unknown'
  /** What every user-visible "AugRank" says: `AugRank` on the apex, the organisation's name on its host. */
  productName: string
  /** The organisation's name, on its host. */
  name: string | null
  /** The organisation's slug, on its host. */
  workspace: string | null
  /** The organisation's origin — its verified custom domain if it has one, else the slug host. */
  workspaceUrl: string | null
  customDomain: string | null
  /** Where the logo is served, fingerprinted; null when there is none and a letter mark stands in. */
  logoUrl: string | null
  /**
   * Whether the workspace's claim is verified. `false` means the host's login
   * refuses everybody until the signup email is confirmed, and `/login` says so
   * instead of offering a form that cannot work.
   */
  verified: boolean
  /** Where to start a trial: the apex's signup page. */
  signupUrl: string
  /**
   * The public origin. A tenant host's *Continue with Google* starts at
   * `{apexUrl}/api/v1/auth/google/start?workspace=<slug>`, because Google
   * sign-in begins and returns on the apex. Empty on the apex itself, so the
   * link is relative there — the same origin the page is on.
   */
  apexUrl: string
  /** The domain workspaces live under — the fixed suffix beside the address field on signup. */
  baseDomain: string
}

/**
 * AugRank's own chrome, and the answer when `/branding` cannot be read.
 *
 * The fallback is the apex rather than an error page because a login screen
 * that refuses to draw until a branding request succeeds is a login screen
 * that goes down with any hiccup between the browser and the API — and the
 * apex brand is always *safe* to show: it invites nobody to sign up under a
 * name that is not theirs, and the API refuses a tenant-host session on its
 * own terms regardless of what the page was wearing.
 */
/**
 * Server-written prose, said in this host's own name.
 *
 * The API's catalogues speak as *AugRank* — `app/seo/findings.CATALOGUE`'s
 * remedies (*"AugRank can write one from the page"*), the notification bodies,
 * the weekly digest's title — and on a tenant host the product is the
 * organisation (`Brand.productName`): the audit screen already says
 * *"Augmentrait Private Limited can fix these"* over a remedy that said
 * *"AugRank cannot change your site's code"* (walkthrough 2026-09-19). The
 * catalogue is closed and the word is a proper noun that appears nowhere else
 * in it, so the substitution is exact; on the apex it is the identity.
 */
export function branded(text: string, brand: Brand): string {
  return brand.productName === 'AugRank' ? text : text.replaceAll('AugRank', brand.productName)
}

export const APEX_BRAND: Brand = {
  host: 'apex',
  productName: 'AugRank',
  name: null,
  workspace: null,
  workspaceUrl: null,
  customDomain: null,
  logoUrl: null,
  verified: true,
  signupUrl: '/signup',
  apexUrl: '',
  baseDomain: 'augrank.com',
}

/**
 * Whether a browser hostname is one the apex brand may honestly stand in for.
 *
 * `BrandProvider` falls back to `APEX_BRAND` when `/branding` cannot be read —
 * but only where the page *is* the apex. On `acme.augrank.com` the fallback
 * would put AugRank's name and *Start a free trial* on a page the tenant bought
 * as their own, which is the leak the provider exists to prevent; there the
 * honest answer is an error and a way to retry. The apex is known by name
 * (production, the local wildcard, and a bare loopback) because before
 * `/branding` answers there is nothing else to know it by; `www.` counts as
 * the apex exactly as `app/tenancy/hosts.parse_host` says.
 */
export function looksLikeApex(hostname: string): boolean {
  const name = hostname.toLowerCase().replace(/^www\./, '')
  return APEX_HOSTNAMES.includes(name)
}

const APEX_HOSTNAMES: readonly string[] = [
  'augrank.com',
  'augrank.localtest.me',
  'localhost',
  '127.0.0.1',
]

export interface BrandValue {
  brand: Brand
  /** Re-read `/branding` — after a logo upload, so the favicon and lockup follow it. */
  refresh: () => Promise<void>
}

export const BrandContext = createContext<BrandValue | null>(null)

export function useBrand(): Brand {
  const value = useContext(BrandContext)
  if (!value) throw new Error('useBrand was called outside <BrandProvider>')
  return value.brand
}

export function useBrandRefresh(): () => Promise<void> {
  const value = useContext(BrandContext)
  if (!value) throw new Error('useBrandRefresh was called outside <BrandProvider>')
  return value.refresh
}

/** The API's snake_case, in the SPA's casing. */
export function brandFromApi(row: Schema<'BrandingOut'>): Brand {
  return {
    host: row.host,
    productName: row.product_name,
    name: row.name ?? null,
    workspace: row.workspace ?? null,
    workspaceUrl: row.workspace_url ?? null,
    customDomain: row.custom_domain ?? null,
    logoUrl: row.logo_url ?? null,
    verified: row.verified,
    signupUrl: row.signup_url,
    apexUrl: row.apex_url,
    baseDomain: row.base_domain,
  }
}
